import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { CONFIG_ITEM_KEYS } from '../ui/prebuilt/config-items/config-item-keys';

/**
 * A contiguous run of top-level nodes that are not synced blocks (source or reference).
 * `from` and `to` are top-level document positions around the run.
 */
export type OutsideRun = { from: number; to: number };

const isSyncBlock = (node: PMNode): boolean =>
	node.type.name === 'bodiedSyncBlock' || node.type.name === 'syncBlock';

export const isImproveDescriptionSyncBlockFixEnabled = (configItemKey: unknown): boolean =>
	configItemKey === CONFIG_ITEM_KEYS.ADD_STRUCTURE &&
	isExperimentEnabled('platform_editor_improve_description_sync_block');

/** Contiguous runs of top-level nodes that are not synced blocks, in document order. */
export function getOutsideSyncBlockRuns(doc: PMNode): OutsideRun[] {
	const runs: OutsideRun[] = [];
	doc.content.forEach((node, offset) => {
		if (isSyncBlock(node)) {
			return;
		}
		const last = runs[runs.length - 1];
		if (last && last.to === offset) {
			last.to = offset + node.nodeSize;
		} else {
			runs.push({ from: offset, to: offset + node.nodeSize });
		}
	});
	return runs;
}

/**
 * The stream range for the first outside run, one position inside its first and
 * last node, which is how the whole-document range [1, size - 1] is shaped today.
 * Undefined when the document is only synced blocks.
 */
export function getFirstOutsideRunRange(doc: PMNode): [number, number] | undefined {
	const run = getOutsideSyncBlockRuns(doc)[0];
	return run && [run.from + 1, run.to - 1];
}

/** The fragment with its top-level synced blocks removed. */
export function withoutSyncBlocks(fragment: Fragment): Fragment {
	const kept: PMNode[] = [];
	fragment.forEach((node) => {
		if (!isSyncBlock(node)) {
			kept.push(node);
		}
	});
	return kept.length === fragment.childCount ? fragment : Fragment.from(kept);
}
